import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Prompt, Link } from 'react-router-dom';
import { Button, Card, CardBody } from 'reactstrap';
import { isEqual } from 'lodash';

import { Breadcrumbs } from 'js/components/Breadcrumbs';
import { Icon, IconStack, LoadingMessage, PanelHeader } from 'js/components';
import { RubricWeightSchemeEditor } from 'js/components/RubricWeights';
import {
  normalizeGuidWeightMap,
  setGuidWeightMapEntry,
} from 'js/components/RubricWeights/rubricWeightSchemeUtils';
import { useNotify } from 'js/hooks/useNotify';
import { ORG_TEAM_RUBRIC_CUSTOM_SCHEME_ID } from 'js/utils/orgTeamRubricScheme';
import { ensureRubricDocumentGuids } from 'js/routes/Rubrics/components/RubricEditor/actions/rubric-editor-actions';
import { cloneDocumentWithGuids } from 'js/routes/Rubrics/components/RubricWeights/rubricWeightsUtils';

import { faFaceThinking } from '@fortawesome/pro-light-svg-icons';
import { faQuestion, faSave } from '@fortawesome/pro-solid-svg-icons';

import {
  findTeamRowInOrgTree,
  orgManageRubricsPath,
} from './orgRubricSchemesUtils';

import rubricWeightStyles from 'js/components/RubricWeights/RubricWeights.module.scss';
import { faCircleNotch } from '@fortawesome/pro-regular-svg-icons';

const UNSAVED_MESSAGE =
  'Hold on - custom weight changes are not saved yet. Leave anyway?';

function OrgTeamRubricCustomWeights({
  workspaceId,
  orgId,
  organization,
  rubric,
  teamId,
  teamRow,
  schemesLoading,
  orgLoading,
  orgLoadFailed,
  onSaveCustomWeights,
}) {
  const notify = useNotify();
  const [submitting, setSubmitting] = useState(false);

  const rubricDocument = useMemo(
    () =>
      cloneDocumentWithGuids(ensureRubricDocumentGuids, rubric?.document ?? {}),
    [rubric?.document]
  );

  const teamMeta = useMemo(
    () => findTeamRowInOrgTree(organization, teamId),
    [organization, teamId]
  );

  const committedWeights = useMemo(
    () => normalizeGuidWeightMap(teamRow?.custom_weights ?? {}),
    [teamRow?.custom_weights]
  );

  const [draftWeights, setDraftWeights] = useState(committedWeights);

  useEffect(() => {
    setDraftWeights(committedWeights);
  }, [committedWeights]);

  const hasChanges = useMemo(
    () => !isEqual(draftWeights, committedWeights),
    [draftWeights, committedWeights]
  );

  const handleWeightChange = useCallback((nodeGuid, rawValue) => {
    const parsed = parseFloat(rawValue);
    const next =
      rawValue === '' || String(rawValue).trim() === ''
        ? null
        : Number.isFinite(parsed)
        ? parsed
        : null;
    setDraftWeights((prev) => setGuidWeightMapEntry(prev, nodeGuid, next));
  }, []);

  const handleSave = useCallback(async () => {
    setSubmitting(true);
    try {
      await onSaveCustomWeights({
        weight_scheme: ORG_TEAM_RUBRIC_CUSTOM_SCHEME_ID,
        custom_weights: draftWeights,
      });
      notify({ type: 'success', message: 'Custom weights saved.' });
    } catch (err) {
      const message =
        err?.response?.data?.errors?.[0]?.detail ??
        err?.response?.data?.message ??
        err?.message ??
        'Unable to save custom weights.';
      notify({ type: 'danger', message });
    } finally {
      setSubmitting(false);
    }
  }, [draftWeights, notify, onSaveCustomWeights]);

  const rubricsPath = orgManageRubricsPath(workspaceId, orgId, rubric?.id);
  const orgName = organization?.name ?? 'Organization';
  const rubricName = rubric?.name ?? ' ';
  const teamName = teamMeta?.name ?? ' ';

  const breadcrumbs = [
    { label: 'Organizations', to: `/w/${workspaceId}/orgs/` },
    {
      label: orgName,
      to: `/w/${workspaceId}/orgs/${encodeURIComponent(orgId)}/manage`,
    },
    {
      label: 'Rubrics',
      to: orgManageRubricsPath(workspaceId, orgId),
    },
  ];

  const pageLoading = orgLoading || !rubric || (schemesLoading && !teamRow);

  return (
    <div>
      <Prompt when={hasChanges} message={UNSAVED_MESSAGE} />
      <PanelHeader size="flex">
        <div className="panel-body-no-label px-4">
          <Breadcrumbs breadcrumbs={breadcrumbs} />
          <div className="content-fluid">
            <h1>
              <Link to={rubricsPath} className="panel-header-link">
                {rubricName}
              </Link>
            </h1>
            <div>
              <label>Custom weights for</label>
              <h2>{teamName}</h2>
            </div>
          </div>
        </div>
      </PanelHeader>

      <div className="content container-fluid">
        {pageLoading ? (
          <Card>
            <CardBody>
              <LoadingMessage iconSize="2x" />
            </CardBody>
          </Card>
        ) : orgLoadFailed && !organization ? (
          <Card>
            <CardBody>
              <div className="media text-muted mt-2">
                <IconStack className="launch-icon mr-3" size="4x" fixedWidth>
                  {/* <Icon icon={faQuestion} /> */}
                  <Icon
                    icon={faQuestion}
                    transform="up-6 left-10 shrink-10 rotate-352"
                  />
                  <Icon
                    icon={faQuestion}
                    transform="up-7 right-10 shrink-10 rotate-10"
                  />
                  <Icon icon={faFaceThinking} />
                </IconStack>
                <div className="media-body">
                  <p>
                    We're having trouble finding the weights for this
                    organization, team and rubric.
                  </p>
                  <p>Please check your links or try again later.</p>
                </div>
              </div>
            </CardBody>
          </Card>
        ) : (
          <>
            <Card>
              <CardBody>
                <RubricWeightSchemeEditor
                  rubricDocument={rubricDocument}
                  draftWeights={draftWeights}
                  committedWeights={committedWeights}
                  onWeightChange={handleWeightChange}
                />
              </CardBody>
            </Card>

            <div className={rubricWeightStyles['save-bar']}>
              <Button
                color="primary"
                type="button"
                disabled={submitting || !hasChanges}
                onClick={handleSave}
              >
                <Icon
                  icon={submitting ? faCircleNotch : faSave}
                  spin={submitting}
                  fixedWidth={true}
                  className="icon-left"
                />
                Save weights
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default OrgTeamRubricCustomWeights;
